/**
 * Heraldic curtain scene transition.
 *
 * Two banner halves sweep in from the sides, meet in the middle behind the
 * player's own crest, hold for a beat, then draw apart onto the new scene.
 * The actual scene swap happens while the screen is covered, so the cut is
 * never visible.
 *
 * This scene always runs on top: it brings itself to the front on create and
 * stops itself when the sequence finishes, so nothing else needs to know it
 * exists. Drive it through `transitionTo()` in src/ui/sceneTransition.ts
 * rather than starting it directly.
 *
 * Input is swallowed for the whole sequence — a click landing on the outgoing
 * scene mid-sweep used to be able to fire a second navigation.
 */
import Phaser from "phaser";
import { GAME_HEIGHT, GAME_WIDTH } from "../config";
import { drawAvatar, isPortraitAvatar } from "../ui/avatar";
import { save } from "../systems/Save";
import { THEME } from "../ui/theme";

export interface TransitionData {
  /** Scene key to start once the curtain is closed. */
  target: string;
  /** Scene key(s) to stop at the same moment. */
  stop?: string[];
  /** Scene key(s) to launch alongside the target (e.g. the in-game HUD). */
  launch?: string[];
  /** Skip the crest — used for the very first curtain out of BootScene. */
  crest?: boolean;
}

const CLOSE_MS = 260;
const HOLD_MS = 130;
const OPEN_MS = 300;

/** Half-width of each banner, with enough overlap that no seam shows. */
const HALF_W = GAME_WIDTH / 2 + 12;

export class TransitionScene extends Phaser.Scene {
  constructor() { super({ key: "TransitionScene", active: false }); }

  create(data: TransitionData): void {
    // Render above every other scene, including the HUD.
    this.scene.bringToTop();

    // Swallow every pointer event for the duration of the sequence.
    const blocker = this.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0)
      .setOrigin(0)
      .setDepth(THEME.depths.tooltip + 10)
      .setInteractive();
    blocker.on("pointerdown", (_p: unknown, __x: unknown, ___y: unknown, ev: Phaser.Types.Input.EventData) => {
      ev.stopPropagation();
    });

    const depth = THEME.depths.tooltip + 20;

    // Left half starts fully off-screen to the left, right half mirrors it.
    const left = this.add.image(-HALF_W, 0, "ui-banner-half")
      .setOrigin(0, 0).setDisplaySize(HALF_W, GAME_HEIGHT).setDepth(depth);
    const right = this.add.image(GAME_WIDTH + HALF_W, 0, "ui-banner-half")
      .setOrigin(0, 0).setDisplaySize(HALF_W, GAME_HEIGHT).setDepth(depth)
      .setFlipX(true);

    // Player's crest rides the seam. Hidden until the halves actually meet.
    // Portraits are omitted as they look out of place on the curtain transition.
    let crest: Phaser.GameObjects.Image | undefined;
    if (data.crest !== false) {
      const avatarId = save.load().avatarId || "gules-lion-or";
      if (!isPortraitAvatar(avatarId)) {
        crest = drawAvatar(this, GAME_WIDTH / 2, GAME_HEIGHT / 2, 132, avatarId);
        crest.setDepth(depth + 1).setAlpha(0).setScale(crest.scale * 0.7);
      }
    }

    // Closing uses easeOut, not easeIn: the banners have to commit immediately
    // and settle as they meet. With easeIn they covered under 2% of the sweep
    // in the first quarter of the duration, so the curtain appeared to do
    // nothing and then slam.
    this.tweens.add({
      targets: left,
      x: 0,
      duration: CLOSE_MS,
      ease: "Quart.easeOut"
    });
    this.tweens.add({
      targets: right,
      x: GAME_WIDTH - HALF_W,
      duration: CLOSE_MS,
      ease: "Quart.easeOut",
      onComplete: () => this.onClosed(data, left, right, crest)
    });
  }

  /** Curtain is shut: swap scenes behind it, flash the crest, then open. */
  private onClosed(
    data: TransitionData,
    left: Phaser.GameObjects.Image,
    right: Phaser.GameObjects.Image,
    crest?: Phaser.GameObjects.Image
  ): void {
    for (const key of data.stop ?? []) {
      if (key !== "TransitionScene") this.scene.stop(key);
    }
    // launch(), NOT start(): ScenePlugin.start() shuts down the *calling*
    // scene, which would tear the curtain down the instant it closed — the
    // new scene then appeared with no opening sweep at all. We stop the
    // outgoing scenes explicitly above, so launching is the correct verb.
    this.scene.launch(data.target);
    for (const key of data.launch ?? []) this.scene.launch(key);
    // start()/launch() put those scenes above us in the display list.
    this.scene.bringToTop();

    if (crest) {
      const full = crest.scale / 0.7;
      this.tweens.add({
        targets: crest,
        alpha: 1,
        scale: full,
        duration: 180,
        ease: "Back.easeOut"
      });
    }

    this.time.delayedCall(HOLD_MS, () => {
      if (crest) {
        this.tweens.add({ targets: crest, alpha: 0, scale: crest.scale * 1.15, duration: OPEN_MS * 0.6, ease: "Quad.easeIn" });
      }
      // Opening accelerates away off-screen, so easeIn is right here.
      this.tweens.add({
        targets: left,
        x: -HALF_W,
        duration: OPEN_MS,
        ease: "Quart.easeIn"
      });
      this.tweens.add({
        targets: right,
        x: GAME_WIDTH + HALF_W,
        duration: OPEN_MS,
        ease: "Quart.easeIn",
        onComplete: () => this.scene.stop()
      });
    });
  }
}
